import type { ContactFormResponse } from '@repo/contact-form-backend/src/lib/types/types';
import {
  CONTACT_FORM_VERSION,
  type ContactFormBody,
  type ContactFormFields,
  type ContactFormMetadata,
} from '@transcend-io/contact-form-schema';
import { BrowserAnalytics } from '@transcend-io/segment/browser';
import { TrackingEvent } from '@transcend-io/segment/core';

import { algoliaInsightsClient, INDEX_MAP } from '../algolia';
import {
  clearViewedAlgoliaObjectIDs,
  getViewedAlgoliaObjectIDs,
} from '../viewed-algolia-objects';
import { FORM_SUBMIT_DEFAULT_ERROR } from './constants';

declare global {
  interface Window {
    /** Helper for DataDog synthetic tests on the contact form */
    _transcendContactFormSuccess?: boolean;
  }
}

interface Options {
  analytics: BrowserAnalytics;
  isSubmissionComplete?: boolean;
  /** Set on /contact field submit and Rails waitlist email capture so abandons forward after the grace period. */
  forwardOnAbandon?: boolean;
}

export async function submitLead(
  fields: ContactFormFields,
  metadata: ContactFormMetadata,
  { analytics, isSubmissionComplete, forwardOnAbandon }: Options,
): Promise<ContactFormResponse> {
  const contactFormBody: ContactFormBody = {
    ...fields,
    ...metadata,
    formVersion: CONTACT_FORM_VERSION,
    isSubmissionComplete: isSubmissionComplete ?? true,
    ...(forwardOnAbandon ? { forwardOnAbandon: true } : {}),
  };

  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      body: JSON.stringify(contactFormBody),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseBody: ContactFormResponse = await response.json();
    if ('error' in responseBody) {
      console.error(response.status, responseBody.error);
      return responseBody;
    }

    try {
      /**
       * Upload conversion data to Algolia for each Algolia object type
       * This allows for Algolia to re-rank search results.
       *
       * For example, if a user viewed the Salesforce integration, then converted,
       * then Salesforce is more likely to show up in search results later.
       */
      algoliaInsightsClient('getUserToken', {}, (err, userToken) => {
        if (err) {
          console.error(err);
          return;
        }

        for (const [algoliaObjectType, index] of Object.entries(INDEX_MAP)) {
          // Get previously-viewed Algolia objects
          const objectIDs = getViewedAlgoliaObjectIDs({
            algoliaObjectType: algoliaObjectType as keyof typeof INDEX_MAP,
          });

          if (objectIDs.length > 0) {
            // Track the conversion in Algolia
            algoliaInsightsClient('convertedObjectIDs', {
              userToken: userToken ? String(userToken) : undefined,
              eventName: TrackingEvent.enum['Contact Form Submitted'],
              index,
              objectIDs,
            });
            // Clear the viewed Algolia objects after conversion
            clearViewedAlgoliaObjectIDs({
              algoliaObjectType: algoliaObjectType as keyof typeof INDEX_MAP,
            });
          }
        }
      });
    } catch (error) {
      console.error(error);
    }

    return responseBody;
  } catch (error) {
    return {
      error: FORM_SUBMIT_DEFAULT_ERROR,
    };
  }
}
